import type {
  Coding,
  QuestionnaireResponseItem,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import {
  DEFAULT_VITALS_UNIT_SYSTEM,
  VitalsUnitSystem,
  VitalsUnitSystems,
} from "@/encounters/components/VitalSigns/utils/vitals-unit-system";
import {
  CELSIUS_TEMPERATURE_UNIT_CODING,
  FAHRENHEIT_TEMPERATURE_UNIT_CODING,
  TemperatureUnitToCode,
} from "@/encounters/constants/vital-sign";

/**
 * Serializes a questionnaire coding answer for labor flowsheet `NativeSelect` fields.
 */
export function readCodingAnswerAsNativeSelectValue(
  item: QuestionnaireResponseItem | undefined,
): string | null {
  const coding = item?.answer?.[0]?.valueCoding;
  if (!coding?.code) {
    return null;
  }
  return JSON.stringify(coding);
}

/**
 * Parses a `NativeSelect` value back into a FHIR Coding; returns null when empty or invalid.
 */
export function parseCodingFromNativeSelect(value: string | null | undefined): Coding | null {
  if (!value) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as Coding;
    if (!parsed?.code) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Returns true when a native-select value points at the "Other" choice display. */
export function isOtherNativeSelectValue(value: string | null | undefined): boolean {
  return parseCodingFromNativeSelect(value)?.display === "Other";
}

/** Mantine `NativeSelect` value for default Fahrenheit temperature unit. */
export const DEFAULT_FAHRENHEIT_TEMPERATURE_UNIT_SELECT_VALUE = JSON.stringify(
  FAHRENHEIT_TEMPERATURE_UNIT_CODING,
);

/** Mantine `NativeSelect` value for default Celsius temperature unit. */
export const DEFAULT_CELSIUS_TEMPERATURE_UNIT_SELECT_VALUE = JSON.stringify(
  CELSIUS_TEMPERATURE_UNIT_CODING,
);

/** Reads the unit system back from a serialized temperature unit coding. */
export function getUnitSystemFromTemperatureUnitSelectValue(
  value: string | null | undefined,
): VitalsUnitSystem {
  const code = parseCodingFromNativeSelect(value)?.code;
  if (!code) return DEFAULT_VITALS_UNIT_SYSTEM;
  return code === TemperatureUnitToCode.F ? VitalsUnitSystems.Imperial : VitalsUnitSystems.Metric;
}

/**
 * Serialized temperature unit coding for a unit system, preferring the expanded ValueSet entry so
 * the value matches one of the rendered options. Falls back to the canonical coding when the
 * ValueSet has not loaded yet.
 */
export function getTemperatureUnitSelectValue(
  unitSystem: VitalsUnitSystem,
  options: ValueSetExpansionContains[],
): string {
  const isImperial = unitSystem === VitalsUnitSystems.Imperial;
  const targetCode = isImperial ? TemperatureUnitToCode.F : TemperatureUnitToCode.C;
  const matchingOption = options.find((option) => option.code === targetCode);

  if (matchingOption) {
    return JSON.stringify({
      system: matchingOption.system ?? "",
      code: matchingOption.code ?? "",
      display: matchingOption.display,
    });
  }

  return isImperial
    ? DEFAULT_FAHRENHEIT_TEMPERATURE_UNIT_SELECT_VALUE
    : DEFAULT_CELSIUS_TEMPERATURE_UNIT_SELECT_VALUE;
}

/**
 * Builds `NativeSelect` options from expanded ValueSet entries, with an empty "Select" row.
 */
export function valueSetContainsToNativeSelectOptions(options: ValueSetExpansionContains[]) {
  return [
    { value: "", label: "Select" },
    ...options.map((option) => ({
      value: JSON.stringify({
        system: option.system ?? "",
        code: option.code ?? "",
        display: option.display,
      }),
      label: option.display ?? option.code ?? "",
    })),
  ];
}
